// @core/stores/calendar/CalendarBookingStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { calendarApi } from '@core/api';
import type {
  BookSlotRequest,
  BookSlotResponse,
  BookingLink,
  CreateBookingLinkRequest,
  GetBookingLinkAvailabilityResponse,
  UpdateBookingLinkRequest,
} from '@core/types/calendar';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class CalendarBookingStore {
  // List state
  items: BookingLink[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: BookingLink | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, BookingLink>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): BookingLink | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: BookingLink | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Book slot
   */
  async BookSlot(slug: string, data: BookSlotRequest): Promise<BookSlotResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.BookSlot(slug, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create booking link
   */
  async CreateBookingLink(data: CreateBookingLinkRequest): Promise<BookingLink> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.CreateBookingLink(data);
      const item = response.booking_link;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete booking link
   */
  async DeleteBookingLink(link_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await calendarApi.DeleteBookingLink(link_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== link_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === link_id) {
          this.currentItem = null;
        }

        this.removeFromCache(link_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get booking link
   */
  async GetBookingLink(link_id: string): Promise<BookingLink> {
    // Check cache first
    const cached = this.cache.get(link_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await calendarApi.GetBookingLink(link_id);
      const item = response.booking_link;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get booking availability
   */
  async GetBookingLinkAvailability(slug: string): Promise<GetBookingLinkAvailabilityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.GetBookingLinkAvailability(slug);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List booking links
   */
  async ListBookingLinks(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await calendarApi.ListBookingLinks(params);

      runInAction(() => {
        this.items = response.booking_links?.links ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update booking link
   */
  async UpdateBookingLink(link_id: string, data: UpdateBookingLinkRequest): Promise<BookingLink> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.UpdateBookingLink(link_id, data);
      const item = response.booking_link;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const calendarBookingStore = new CalendarBookingStore();
